import type React from "react";
import { useEffect } from "react";
import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormDialog,
  FormDialogCancel,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Spinner,
} from "@capacit-com/lara-law-shared-ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

const formSchema = z.object({
  canonicalTerm: z.string().trim().min(1, { message: "Udfyld kanonisk term" }),
});

type FormValues = z.infer<typeof formSchema>;

type CreateSynonymGroupDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateGroup: (canonicalTerm: string) => void;
  disabled: boolean;
};

const CreateSynonymGroupDialog: React.FC<CreateSynonymGroupDialogProps> = ({
  open,
  onOpenChange,
  onCreateGroup,
  disabled,
}) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { canonicalTerm: "" },
  });

  // The dialog stays mounted between opens, so the last term would otherwise
  // still be in the field the next time it opens.
  useEffect(() => {
    if (open) form.reset({ canonicalTerm: "" });
  }, [open, form]);

  const handleSubmit = form.handleSubmit(({ canonicalTerm }) => {
    onCreateGroup(canonicalTerm);
    form.reset({ canonicalTerm: "" });
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      isDirty={form.formState.isDirty}
    >
      <DialogHeader>
        <DialogTitle>Opret synonymgruppe</DialogTitle>
        <DialogDescription>
          Opret en ny synonymgruppe med en kanonisk term. Du kan tilføje
          synonymer efter oprettelse af gruppen.
        </DialogDescription>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <FormField
              control={form.control}
              name="canonicalTerm"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Kanonisk term</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="f.eks. smartphone" />
                  </FormControl>
                  <FormDescription>
                    Det primære term som synonymer vil blive mappet til
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <DialogFooter>
            <FormDialogCancel disabled={disabled} />
            <Button type="submit" disabled={disabled}>
              {!disabled ? (
                "Opret gruppe"
              ) : (
                <Spinner className="size-4 text-primary-foreground" />
              )}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </FormDialog>
  );
};

export default CreateSynonymGroupDialog;
